import { useEffect, useMemo, useRef, useState } from "react";
import { BedDouble, BookOpen, Compass, Sparkles, ConciergeBell, Utensils, MapPin, Images } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * StickyHotelNav — sticky sub-navigation for hotel detail pages.
 *
 * Anchors: Rooms · Services · Spa · Food · Nearby (only rendered when the
 * matching section id exists on the page). Highlights the currently visible
 * section via IntersectionObserver. On mobile, becomes a horizontal
 * scroll-snap strip so all anchors remain reachable with a single swipe.
 *
 * When `isBivouac` is true, the "Rooms" anchor swaps to "Haimas" so the
 * three desert-camp pages surface the Sahrawi vocabulary the rest of
 * the page uses.
 */
export default function StickyHotelNav({ availableIds, isBivouac = false }) {
  const { lang } = useLanguage();
  const pick = (m, l) => (m && (m[l] || m.en)) || "";

  const ALL = useMemo(
    () => [
      {
        id: "rooms",
        icon: BedDouble,
        label: isBivouac
          ? { en: "Haimas", fr: "Haimas", es: "Haimas" }
          : { en: "Rooms", fr: "Chambres", es: "Habitaciones" },
      },
      {
        id: "gallery",
        icon: Images,
        label: { en: "Gallery", fr: "Galerie", es: "Galería" },
      },
      {
        id: "brochure",
        icon: BookOpen,
        label: { en: "Brochure", fr: "Catalogue", es: "Catálogo" },
      },
      {
        id: "experiences",
        icon: Compass,
        label: { en: "Experiences", fr: "Expériences", es: "Experiencias" },
      },
      {
        id: "services",
        icon: ConciergeBell,
        label: { en: "Services", fr: "Services", es: "Servicios" },
      },
      {
        id: "spa",
        icon: Sparkles,
        label: { en: "Spa", fr: "Spa", es: "Spa" },
      },
      {
        id: "food",
        icon: Utensils,
        label: { en: "Gastronomy", fr: "Gastronomie", es: "Gastronomía" },
      },
      {
        id: "destination",
        icon: MapPin,
        label: { en: "Nearby", fr: "À proximité", es: "Cerca de aquí" },
      },
    ],
    []
  );

  // Only render anchors whose section is actually present in the DOM.
  const items = useMemo(() => {
    if (availableIds && availableIds.length) {
      const set = new Set(availableIds);
      return ALL.filter((i) => set.has(i.id));
    }
    return ALL;
  }, [ALL, availableIds]);

  const [activeId, setActiveId] = useState(items[0]?.id || null);
  const stripRef = useRef(null);

  useEffect(() => {
    if (!items.length) return;
    const observers = [];
    // Use a slice of the viewport just below the sticky header as the
    // "active" band. Section is active when its top crosses that band.
    const rootMargin = "-140px 0px -55% 0px";
    const io = new IntersectionObserver(
      (entries) => {
        // Prefer the entry that is intersecting AND highest in the viewport
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin, threshold: 0 }
    );
    items.forEach((it) => {
      const el = document.getElementById(it.id);
      if (el) io.observe(el);
    });
    observers.push(io);
    return () => observers.forEach((o) => o.disconnect());
  }, [items]);

  // Keep the active pill in view on mobile (scroll it into the strip)
  useEffect(() => {
    if (!activeId || !stripRef.current) return;
    const btn = stripRef.current.querySelector(`[data-nav-target="${activeId}"]`);
    if (btn && typeof btn.scrollIntoView === "function") {
      btn.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [activeId]);

  if (!items.length) return null;

  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    // Offset for the fixed header + this sticky nav (~180px total)
    const y = el.getBoundingClientRect().top + window.scrollY - 170;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  return (
    <nav
      className="sticky top-[116px] z-30 bg-[#FDFBF7]/95 backdrop-blur-md border-b border-[#2C2621]/10"
      data-testid="hotel-sticky-nav"
      aria-label="Hotel section navigation"
    >
      <div
        ref={stripRef}
        className="max-w-7xl mx-auto px-4 md:px-12 flex items-center gap-1 md:gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory md:snap-none"
      >
        {items.map((it) => {
          const Icon = it.icon;
          const isActive = activeId === it.id;
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => jumpTo(it.id)}
              data-nav-target={it.id}
              data-testid={`hotel-nav-${it.id}`}
              data-active={isActive}
              className={`snap-start shrink-0 group inline-flex items-center gap-2 py-3.5 md:py-4 px-3 md:px-4 border-b-2 transition-colors whitespace-nowrap text-[11px] md:text-xs tracking-[0.22em] uppercase ${
                isActive
                  ? "border-[#C16542] text-[#C16542]"
                  : "border-transparent text-[#5C5248] hover:text-[#2C2621]"
              }`}
            >
              <Icon
                className={`w-4 h-4 transition-colors ${
                  isActive ? "text-[#C16542]" : "text-[#5C5248] group-hover:text-[#2C2621]"
                }`}
                strokeWidth={1.5}
              />
              <span>{pick(it.label, lang)}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
